import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import {
  Building2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  Download,
  ExternalLink,
  FilePenLine,
  FileText,
  Folder,
  FolderInput,
  FolderPlus,
  Globe,
  Grid2X2,
  List,
  Plus,
  ScrollText,
  Search,
  Sparkles,
  Trash2,
  Upload,
  Video,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { MaterialsFilterPopover } from "@/components/materials/MaterialsFilterPopover";
import { MaterialsFolderActionsMenu } from "@/components/materials/MaterialsFolderActionsMenu";
import { MaterialsFolderTree } from "@/components/materials/MaterialsFolderTree";
import { MaterialsStorageBar } from "@/components/materials/MaterialsStorageBar";
import { ConfirmDialog } from "@/components/shell/ConfirmDialog";
import { ButtonGroup } from "@/components/ui/button-group";
import { ApiError } from "@/lib/api";
import {
  createMaterialsApi,
  type MaterialsAuditEvent,
  type MaterialsFolder,
  type MaterialsItem,
} from "@/lib/materials-api";
import {
  canManageFolder,
  folderNodeKind,
  materialsContextCopy,
  type MaterialsPageContext,
} from "@/lib/materials-folder-tree";
import { useMaterialsUploadQueue } from "@/lib/materials-upload-queue";
import {
  buildMaterialUsageListRows,
  extractInUseUsages,
  formatMaterialUsagesBlockReason,
  materialsPublicIdCopySuccessMessage,
  selectedMaterialItemIds,
} from "@/lib/materials-item-ui";
import { useKbCanWrite } from "@/lib/kb-permissions";
import { MessageResponse } from "@/components/ai-elements/message";
import {
  brandIdForCreate,
  buildMaterialsFilterTagSpecs,
  classifyFileType,
  collectFolderSubtreeIds,
  emptyClientFilters,
  folderMatchesClientFilters,
  hasActiveFilters,
  itemMatchesClientFilters,
  type MaterialsClientFilters,
  type MaterialsFilterTagClear,
} from "@/lib/materials-filters";
import { previewKind, previewKindLabel } from "@/lib/materials-preview-kind";
import {
  externalVideoEmbedUrl,
  isExternalVideoItem,
} from "@/lib/materials-external-video";
import {
  disabledTargetFolderIds,
  isMoveNoOp,
  moveSuccessToast,
  remainsInWorkspace,
} from "@/lib/materials-move";
import {
  folderIsEmpty,
  summarizeMixedBulkDelete,
} from "@/lib/materials-folder-ui";
import { kbMaterialsPath } from "@/lib/kb-routes";
import { cn } from "@/lib/utils";

type ViewMode = "list" | "grid";

/** 预览弹窗固定高度：视口内尽量撑满，超大屏封顶，切换素材类型时不抖动 */
const MATERIALS_PREVIEW_DIALOG_CLASS =
  "flex h-[min(calc(100dvh-2rem),56rem)] max-h-[min(calc(100dvh-2rem),56rem)] flex-col gap-4 overflow-hidden sm:max-w-3xl";

type Props = {
  accountId: string;
  context: MaterialsPageContext;
};

const ROOT_KEY = "root";

/** 「提取到知识库」尚未接通；对话框仅示意，勿假装已写入 KB */
const EXTRACT_COMING_SOON_FACTS = [
  { id: "fact-1", text: "公司成立于 2018 年" },
  { id: "fact-2", text: "总部地址：上海市浦东新区示例路 88 号" },
  { id: "fact-3", text: "主营：品牌数字化增长服务" },
  { id: "fact-4", text: "对外简称：C456 示例企业" },
];

function ensureMdFilename(name: string): string {
  const trimmed = name.trim() || "未命名文案";
  return trimmed.toLowerCase().endsWith(".md") ? trimmed : `${trimmed}.md`;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString("zh-CN", {
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function isMarkdown(item: MaterialsItem): boolean {
  return classifyFileType(item) === "markdown";
}

function auditActionLabel(action: string): string {
  const map: Record<string, string> = {
    upload: "上传",
    create_markdown: "新建文案",
    create_external_video: "添加视频",
    download: "下载",
    tag: "打标",
    rename: "改名",
    move: "移动",
    delete: "删除",
  };
  return map[action] ?? action;
}

function FolderGlyph({
  folder,
  className,
}: {
  folder: MaterialsFolder;
  className?: string;
}) {
  const kind = folderNodeKind(folder);
  const Icon = kind === "brand_root" ? Building2 : kind === "brand_site_root" ? Globe : Folder;
  return (
    <Icon
      className={cn(
        "shrink-0",
        kind === "normal" ? "text-muted-foreground" : "text-primary",
        className,
      )}
    />
  );
}

const AUDIT_ITEM_TARGET = "Materials::Item";
const AUDIT_FOLDER_TARGET = "Materials::Folder";

function auditTargetKey(event: MaterialsAuditEvent): string {
  return `${event.target_type ?? ""}:${event.target_id ?? ""}`;
}

/** 操作记录是否指向可打开的素材/文件夹目标（删除类默认不可点） */
function isOpenableAuditTarget(event: MaterialsAuditEvent): boolean {
  if (event.target_id == null || !event.target_type) return false;
  if (event.action === "delete") return false;
  return (
    event.target_type === AUDIT_ITEM_TARGET ||
    event.target_type === AUDIT_FOLDER_TARGET
  );
}

function errorMessage(caught: unknown, fallback: string): string {
  return caught instanceof ApiError ? caught.message : fallback;
}

type FilterTagValue = {
  key: string;
  label: string;
  onRemove: () => void;
};

type FilterTag = {
  key: string;
  facet: string;
  values: FilterTagValue[];
};

export function MaterialsShell({ accountId, context }: Props) {
  const routeBrandId = context.brandId;
  const contextCopy = materialsContextCopy(context);
  const canWrite = useKbCanWrite();
  const { enqueueUploads, subscribeItemDone } = useMaterialsUploadQueue();
  const apiClient = useMemo(() => createMaterialsApi(accountId), [accountId]);

  const [clientFilters, setClientFilters] = useState<MaterialsClientFilters>(() =>
    emptyClientFilters(),
  );
  const [filterOpen, setFilterOpen] = useState(false);
  const [folderId, setFolderId] = useState<number | null>(null);
  /** false：仅当前目录文件；true：含所有子孙目录中的文件 */
  const [includeDescendants, setIncludeDescendants] = useState(false);
  const [nameQuery, setNameQuery] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [folders, setFolders] = useState<MaterialsFolder[]>([]);
  const [items, setItems] = useState<MaterialsItem[]>([]);
  const [workRootId, setWorkRootId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [auditOpen, setAuditOpen] = useState(false);
  const [auditEvents, setAuditEvents] = useState<MaterialsAuditEvent[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  /** 点击操作记录后确认已不存在的 target，用于去掉可点样式 */
  const [auditGoneTargets, setAuditGoneTargets] = useState<Set<string>>(
    () => new Set(),
  );

  const [extractOpen, setExtractOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeTitle, setComposeTitle] = useState("");
  const [composeBody, setComposeBody] = useState("");
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [videoInput, setVideoInput] = useState("");
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [factChecks, setFactChecks] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(EXTRACT_COMING_SOON_FACTS.map((f) => [f.id, true])),
  );

  const [previewItem, setPreviewItem] = useState<MaterialsItem | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewMetaLoading, setPreviewMetaLoading] = useState(false);
  /** getItem 失败时 true：无法确认引用，禁用删除 */
  const [previewUsagesLoadFailed, setPreviewUsagesLoadFailed] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewObjectUrl, setPreviewObjectUrl] = useState<string | null>(null);
  const [previewExternalEmbedUrl, setPreviewExternalEmbedUrl] = useState<string | null>(null);
  const [previewText, setPreviewText] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveTargetId, setMoveTargetId] = useState<number | null>(null);
  const [moving, setMoving] = useState(false);
  const [renameFolderOpen, setRenameFolderOpen] = useState(false);
  const [renameFolderTarget, setRenameFolderTarget] = useState<MaterialsFolder | null>(
    null,
  );
  const [renameFolderName, setRenameFolderName] = useState("");
  const [renamingFolder, setRenamingFolder] = useState(false);
  const [folderDeleteConfirmOpen, setFolderDeleteConfirmOpen] = useState(false);
  const [folderDeleteIds, setFolderDeleteIds] = useState<number[]>([]);
  const [folderDeleting, setFolderDeleting] = useState(false);
  const previewObjectUrlRef = useRef<string | null>(null);
  /** 关闭或切换预览时递增，丢弃过期的 content 请求结果 */
  const previewRequestIdRef = useRef(0);

  const fileInputRef = useRef<HTMLInputElement>(null);
  /** 工具栏整行：过滤面板宽度与水平定位对齐此节点 */
  const toolbarRowRef = useRef<HTMLDivElement>(null);

  const listContext = useMemo(
    () => ({
      context: context.kind,
      brandId: context.brandId,
      brandSiteId: context.brandSiteId,
    }),
    [context.brandId, context.brandSiteId, context.kind],
  );

  const loadItemsForView = useCallback(
    async (nextFolders: MaterialsFolder[], nextWorkRootId: number | null) => {
      const requestedFolderIds: Array<number | null> = [folderId];
      if (includeDescendants) {
        const childrenOf = new Map<number | null, number[]>();
        for (const folder of nextFolders) {
          const children = childrenOf.get(folder.parent_id) ?? [];
          children.push(folder.id);
          childrenOf.set(folder.parent_id, children);
        }
        const pending = [...(childrenOf.get(folderId ?? nextWorkRootId) ?? [])];
        const seen = new Set<number>();
        while (pending.length > 0) {
          const id = pending.shift()!;
          if (seen.has(id)) continue;
          seen.add(id);
          requestedFolderIds.push(id);
          pending.push(...(childrenOf.get(id) ?? []));
        }
      }

      const payloads = await Promise.all(
        requestedFolderIds.map((requestedFolderId) =>
          apiClient.listItems({
            ...listContext,
            folderId: requestedFolderId,
          }),
        ),
      );
      return [...new Map(payloads.flatMap((payload) => payload.items).map((item) => [item.id, item])).values()];
    },
    [apiClient, folderId, includeDescendants, listContext],
  );

  const reloadLibrary = useCallback(
    async (opts: { silent?: boolean } = {}) => {
      if (!opts.silent) setLoading(true);
      try {
        const { folders: nextFolders, work_root_id: nextWorkRootId } =
          await apiClient.listFolders(listContext);
        const nextItems = await loadItemsForView(nextFolders, nextWorkRootId);
        setFolders(nextFolders);
        setItems(nextItems);
        setWorkRootId(nextWorkRootId);
        setFolderId((current) =>
          current != null && !nextFolders.some((f) => f.id === current) ? null : current,
        );
      } catch (caught) {
        toast.error(errorMessage(caught, "无法加载素材库"));
      } finally {
        if (!opts.silent) setLoading(false);
      }
    },
    [apiClient, listContext, loadItemsForView],
  );

  useEffect(() => {
    setClientFilters(emptyClientFilters());
    setFolderId(null);
    setNameQuery("");
    setSelected(new Set());
    setWorkRootId(null);
  }, [accountId, context.brandId, context.brandSiteId, context.kind]);

  useEffect(() => {
    return subscribeItemDone((doneAccountId) => {
      if (doneAccountId !== accountId) return;
      void reloadLibrary({ silent: true });
    });
  }, [accountId, reloadLibrary, subscribeItemDone]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { folders: nextFolders, work_root_id: nextWorkRootId } =
          await apiClient.listFolders(listContext);
        const nextItems = await loadItemsForView(nextFolders, nextWorkRootId);
        if (cancelled) return;
        setFolders(nextFolders);
        setItems(nextItems);
        setWorkRootId(nextWorkRootId);
      } catch (caught) {
        if (!cancelled) toast.error(errorMessage(caught, "无法加载素材库"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accountId, apiClient, listContext, loadItemsForView]);

  const folderById = useMemo(
    () => new Map(folders.map((f) => [f.id, f])),
    [folders],
  );
  const currentStorageFolderId = folderId ?? workRootId;

  const [resolvedOperators, setResolvedOperators] = useState<
    { id: number; name: string }[]
  >([]);

  const handleOperatorsResolved = useCallback(
    (ops: { id: number; name: string }[]) => {
      setResolvedOperators(ops);
    },
    [],
  );

  const operatorOptions = useMemo(() => {
    const map = new Map<number, string>();
    for (const op of resolvedOperators) map.set(op.id, op.name);
    for (const item of items) {
      const name = item.uploader_name?.trim() || `用户 ${item.uploaded_by_user_id}`;
      const existing = map.get(item.uploaded_by_user_id);
      if (!existing || existing.startsWith("用户 ")) {
        map.set(item.uploaded_by_user_id, name);
      }
    }
    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, "zh-CN"));
  }, [items, resolvedOperators]);

  const childFolders = useMemo(
    () =>
      folders
        .filter((f) => f.parent_id === currentStorageFolderId)
        .filter((f) => folderMatchesClientFilters(f, clientFilters))
        .sort((a, b) => a.id - b.id),
    [folders, currentStorageFolderId, clientFilters],
  );

  const folderSubtreeIds = useMemo(
    () => collectFolderSubtreeIds(folderId, folders),
    [folderId, folders],
  );

  const visibleItems = useMemo(() => {
    const inFolder = includeDescendants
      ? (item: MaterialsItem) => folderSubtreeIds.has(item.folder_id)
      : (item: MaterialsItem) => item.folder_id === currentStorageFolderId;
    return items
      .filter(inFolder)
      .filter((item) => itemMatchesClientFilters(item, clientFilters))
      .sort((a, b) => b.id - a.id);
  }, [
    items,
    currentStorageFolderId,
    clientFilters,
    includeDescendants,
    folderSubtreeIds,
  ]);

  function folderLabelForItem(item: MaterialsItem): string | null {
    if (!includeDescendants || item.folder_id === currentStorageFolderId) return null;
    if (item.folder_id == null || item.folder_id === workRootId) {
      return contextCopy.rootLabel;
    }
    return folderById.get(item.folder_id)?.name ?? null;
  }

  const nameQueryNormalized = nameQuery.trim().toLowerCase();

  const filteredChildFolders = useMemo(() => {
    if (!nameQueryNormalized) return childFolders;
    return childFolders.filter((f) => f.name.toLowerCase().includes(nameQueryNormalized));
  }, [childFolders, nameQueryNormalized]);

  const filteredVisibleItems = useMemo(() => {
    if (!nameQueryNormalized) return visibleItems;
    return visibleItems.filter((item) => item.name.toLowerCase().includes(nameQueryNormalized));
  }, [visibleItems, nameQueryNormalized]);

  /** 列表视图当前可见行的选择键（文件夹 + 文件） */
  const visibleSelectKeys = useMemo(() => {
    const keys: string[] = [];
    for (const f of filteredChildFolders) {
      if (canManageFolder(f)) keys.push(`folder:${f.id}`);
    }
    for (const item of filteredVisibleItems) keys.push(String(item.id));
    return keys;
  }, [filteredChildFolders, filteredVisibleItems]);

  const selectedVisibleCount = useMemo(
    () => visibleSelectKeys.reduce((n, key) => n + (selected.has(key) ? 1 : 0), 0),
    [visibleSelectKeys, selected],
  );
  const allVisibleSelected =
    visibleSelectKeys.length > 0 && selectedVisibleCount === visibleSelectKeys.length;
  const someVisibleSelected =
    selectedVisibleCount > 0 && selectedVisibleCount < visibleSelectKeys.length;

  const selectedItemIds = useMemo(() => selectedMaterialItemIds(selected), [selected]);
  const selectedFolderIds = useMemo(() => {
    const ids: number[] = [];
    for (const key of selected) {
      if (!key.startsWith("folder:")) continue;
      const id = Number(key.slice("folder:".length));
      if (Number.isFinite(id) && id > 0) ids.push(id);
    }
    return ids;
  }, [selected]);
  const selectedFolderCount = selectedFolderIds.length;
  const moveDisabledFolderIds = useMemo(
    () => disabledTargetFolderIds(selectedFolderIds, folders),
    [selectedFolderIds, folders],
  );

  /** Dialog 内缩进目录行（含根）；带 binding_kind 以便系统根用不同图标 */
  const movePickerRows = useMemo(() => {
    type Row = {
      id: number | null;
      name: string;
      depth: number;
      disabled: boolean;
      folder: MaterialsFolder | null;
    };
    const byParent = new Map<number | null, MaterialsFolder[]>();
    for (const folder of folders) {
      const list = byParent.get(folder.parent_id) ?? [];
      list.push(folder);
      byParent.set(folder.parent_id, list);
    }
    for (const list of byParent.values()) {
      list.sort((a, b) => a.id - b.id);
    }
    const rows: Row[] = [
      {
        id: null,
        name: contextCopy.rootLabel,
        depth: 0,
        disabled: false,
        folder: null,
      },
    ];
    function walk(parentId: number | null, depth: number) {
      for (const folder of byParent.get(parentId) ?? []) {
        rows.push({
          id: folder.id,
          name: folder.name,
          depth,
          disabled: moveDisabledFolderIds.has(folder.id),
          folder,
        });
        walk(folder.id, depth + 1);
      }
    }
    walk(workRootId, 1);
    return rows;
  }, [contextCopy.rootLabel, folders, moveDisabledFolderIds, workRootId]);

  const folderEmptyCheck = useCallback(
    (folderId: number) => folderIsEmpty(folderId, folders, items),
    [folders, items],
  );

  const selectAllRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someVisibleSelected;
    }
  }, [someVisibleSelected]);

  const breadcrumb = useMemo(() => {
    const chain: { id: number | null; name: string }[] = [
      { id: null, name: contextCopy.rootLabel },
    ];
    if (folderId == null) return chain;
    const stack: MaterialsFolder[] = [];
    let cur: number | null = folderId;
    while (cur != null && cur !== workRootId) {
      const f = folderById.get(cur);
      if (!f) break;
      stack.unshift(f);
      cur = f.parent_id;
    }
    for (const f of stack) chain.push({ id: f.id, name: f.name });
    return chain;
  }, [contextCopy.rootLabel, folderById, folderId, workRootId]);

  const libraryEmpty = !loading && folders.length === 0 && items.length === 0;
  const folderEmpty = !loading && childFolders.length === 0 && visibleItems.length === 0;
  const searchEmpty =
    !loading &&
    !folderEmpty &&
    filteredChildFolders.length === 0 &&
    filteredVisibleItems.length === 0;

  function navigateFolder(id: number | null) {
    setFolderId(id);
    setNameQuery("");
    setSelected(new Set());
  }

  function applyFilterTagClear(clear: MaterialsFilterTagClear) {
    setClientFilters((prev) => {
      switch (clear.kind) {
        case "operators": {
          const drop = new Set(clear.operatorIds);
          return {
            ...prev,
            operatorIds: prev.operatorIds.filter((id) => !drop.has(id)),
          };
        }
        case "createdRange":
          return { ...prev, createdRange: { from: "", to: "" } };
        case "updatedRange":
          return { ...prev, updatedRange: { from: "", to: "" } };
        case "sizeRange":
          return {
            ...prev,
            sizeRange: { min: "", max: "", unit: prev.sizeRange.unit },
          };
        case "fileTypes": {
          const drop = new Set(clear.fileTypes);
          return {
            ...prev,
            fileTypes: prev.fileTypes.filter((ft) => !drop.has(ft)),
          };
        }
        default:
          return prev;
      }
    });
  }

  function toggleSelect(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  /** 全选 / 全不选当前列表可见项（保留非当前可见行的已选） */
  function toggleSelectAllVisible() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const key of visibleSelectKeys) next.delete(key);
      } else {
        for (const key of visibleSelectKeys) next.add(key);
      }
      return next;
    });
  }

  function openExtract() {
    if (selected.size === 0) {
      toast.message("请先勾选文件或文件夹");
      return;
    }
    setExtractOpen(true);
  }

  function openBulkDelete() {
    if (!canWrite) return;
    if (selectedItemIds.length === 0 && selectedFolderIds.length === 0) {
      toast.message("请先勾选文件或文件夹");
      return;
    }
    setBulkDeleteConfirmOpen(true);
  }

  function openMove() {
    if (!canWrite) return;
    if (selectedItemIds.length === 0 && selectedFolderIds.length === 0) {
      toast.message("请先勾选文件或文件夹");
      return;
    }
    setMoveTargetId(null);
    setMoveOpen(true);
  }

  function beginMoveFolder(folder: MaterialsFolder) {
    if (!canWrite) return;
    setSelected(new Set([`folder:${folder.id}`]));
    setMoveTargetId(null);
    setMoveOpen(true);
  }

  function beginRenameFolder(folder: MaterialsFolder) {
    if (!canWrite) return;
    setRenameFolderTarget(folder);
    setRenameFolderName(folder.name);
    setRenameFolderOpen(true);
  }

  async function confirmRenameFolder() {
    if (!canWrite || !renameFolderTarget) return;
    const name = renameFolderName.trim();
    if (!name) {
      toast.error("名称不能为空");
      return;
    }
    setRenamingFolder(true);
    try {
      await apiClient.patchFolder(renameFolderTarget.id, { name });
      setRenameFolderOpen(false);
      setRenameFolderTarget(null);
      toast.success("文件夹已重命名");
      await reloadLibrary({ silent: true });
    } catch (caught) {
      toast.error(errorMessage(caught, "重命名失败"));
    } finally {
      setRenamingFolder(false);
    }
  }

  function beginDeleteFolder(folder: MaterialsFolder) {
    if (!canWrite) return;
    if (!folderEmptyCheck(folder.id)) {
      toast.message(`「${folder.name}」里还有文件或子文件夹，请先清空或移走`);
      return;
    }
    setFolderDeleteIds([folder.id]);
    setFolderDeleteConfirmOpen(true);
  }

  async function confirmDeleteFolders() {
    if (!canWrite || folderDeleteIds.length === 0) return;
    setFolderDeleting(true);
    try {
      const id = folderDeleteIds[0]!;
      if (!folderEmptyCheck(id)) {
        const folder = folders.find((f) => f.id === id);
        toast.message(
          folder
            ? `「${folder.name}」里还有文件或子文件夹，请先清空或移走`
            : "文件夹不为空",
        );
        setFolderDeleteConfirmOpen(false);
        return;
      }
      await apiClient.deleteFolder(id);
      setFolderDeleteConfirmOpen(false);
      setFolderDeleteIds([]);
      setSelected(new Set());
      toast.success("已删除");
      await reloadLibrary({ silent: true });
    } catch (caught) {
      toast.error(errorMessage(caught, "删除失败"));
    } finally {
      setFolderDeleting(false);
    }
  }

  async function confirmMove() {
    if (!canWrite) return;
    if (selectedItemIds.length === 0 && selectedFolderIds.length === 0) return;
    if (moveTargetId != null && moveDisabledFolderIds.has(moveTargetId)) {
      toast.error("不能移动到自身或其子目录");
      return;
    }

    const apiMoveTargetId = moveTargetId ?? workRootId;
    const count = selectedItemIds.length + selectedFolderIds.length;

    if (
      isMoveNoOp(selectedItemIds, selectedFolderIds, apiMoveTargetId, items, folders)
    ) {
      setMoveOpen(false);
      toast.message(moveSuccessToast(0, false));
      return;
    }

    const leftCurrentWorkspace = !remainsInWorkspace(
      context,
      apiMoveTargetId,
      workRootId,
      folders,
    );

    setMoving(true);
    try {
      await apiClient.moveResources({
        itemIds: selectedItemIds,
        folderIds: selectedFolderIds,
        targetFolderId: apiMoveTargetId,
      });
      setMoveOpen(false);
      setSelected(new Set());
      toast.success(moveSuccessToast(count, leftCurrentWorkspace));
      await reloadLibrary({ silent: true });
    } catch (caught) {
      toast.error(errorMessage(caught, "移动失败"));
    } finally {
      setMoving(false);
    }
  }

  async function confirmBulkDelete() {
    if (!canWrite) return;
    if (selectedItemIds.length === 0 && selectedFolderIds.length === 0) return;
    setBulkDeleting(true);
    try {
      let itemsDeleted = 0;
      let itemsBlocked = 0;
      let itemsFailed = 0;
      let foldersDeleted = 0;
      let foldersBlocked = 0;
      let foldersFailed = 0;

      if (selectedItemIds.length > 0) {
        const result = await apiClient.bulkDeleteItems(selectedItemIds);
        itemsDeleted = result.deleted.length;
        itemsBlocked = result.blocked.length;
        itemsFailed = result.failed.length;
      }
      if (selectedFolderIds.length > 0) {
        const result = await apiClient.bulkDeleteFolders(selectedFolderIds);
        foldersDeleted = result.deleted.length;
        foldersBlocked = result.blocked.length;
        foldersFailed = result.failed.length;
      }

      setBulkDeleteConfirmOpen(false);
      setSelected(new Set());
      const summary = summarizeMixedBulkDelete({
        itemsDeleted,
        itemsBlocked,
        itemsFailed,
        foldersDeleted,
        foldersBlocked,
        foldersFailed,
      });
      if (summary.tone === "success") toast.success(summary.message);
      else if (summary.tone === "warning") toast.warning(summary.message);
      else toast.error(summary.message);
      if (itemsDeleted > 0 || foldersDeleted > 0) {
        await reloadLibrary({ silent: true });
      }
    } catch (caught) {
      toast.error(errorMessage(caught, "删除失败"));
    } finally {
      setBulkDeleting(false);
    }
  }

  function confirmExtractComingSoon() {
    setExtractOpen(false);
    toast.message("提取到知识库即将支持，本版本不会写入知识库");
  }

  async function openAudit() {
    setAuditOpen(true);
    setAuditLoading(true);
    setAuditGoneTargets(new Set());
    try {
      const { audit_events } = await apiClient.listAuditEvents();
      setAuditEvents(audit_events);
    } catch (caught) {
      toast.error(errorMessage(caught, "无法加载操作记录"));
    } finally {
      setAuditLoading(false);
    }
  }

  function openComposeDialog() {
    setComposeTitle("");
    setComposeBody("");
    setComposeOpen(true);
  }

  function openVideoDialog() {
    setVideoInput("");
    setVideoDialogOpen(true);
  }

  async function saveExternalVideo() {
    const input = videoInput.trim();
    if (!input) {
      toast.error("地址不对");
      return;
    }
    setBusy(true);
    try {
      await apiClient.createExternalVideo({
        external_video_input: input,
        folder_id: currentStorageFolderId,
        brand_id: brandIdForCreate(context),
      });
      setVideoDialogOpen(false);
      toast.success("已添加视频");
      await reloadLibrary({ silent: true });
    } catch (caught) {
      toast.error(errorMessage(caught, "添加失败"));
    } finally {
      setBusy(false);
    }
  }

  async function saveMarkdownCompose() {
    if (!composeTitle.trim()) {
      toast.error("请填写文件名");
      return;
    }
    const filename = ensureMdFilename(composeTitle);
    setBusy(true);
    try {
      await apiClient.createMarkdown({
        name: filename,
        body: composeBody,
        folder_id: currentStorageFolderId,
        brand_id: brandIdForCreate(context),
      });
      setComposeOpen(false);
      toast.success(`已保存「${filename}」`);
      await reloadLibrary({ silent: true });
    } catch (caught) {
      toast.error(errorMessage(caught, "保存文案失败"));
    } finally {
      setBusy(false);
    }
  }

  function onFileChosen(fileList: FileList | null) {
    if (!fileList?.length) return;
    const files = Array.from(fileList);
    enqueueUploads({
      accountId,
      files,
      folderId: currentStorageFolderId,
      brandId: brandIdForCreate(context),
      materialsPath:
        context.kind === "brand_site" && context.brandId && context.brandSiteId
          ? `/accounts/${accountId}/brands/${context.brandId}/sites/${context.brandSiteId}/materials`
          : kbMaterialsPath({ accountId, brandId: routeBrandId }),
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  async function createFolder() {
    const name = newFolderName.trim();
    if (!name) {
      toast.message("请填写文件夹名称");
      return;
    }
    setBusy(true);
    try {
      const { folder } = await apiClient.createFolder({
        name,
        parent_id: currentStorageFolderId,
        brand_id: brandIdForCreate(context),
      });
      setFolderDialogOpen(false);
      setNewFolderName("");
      toast.success(`已创建文件夹「${folder.name}」`);
      await reloadLibrary({ silent: true });
      setFolderId(folder.id);
    } catch (caught) {
      toast.error(errorMessage(caught, "创建文件夹失败"));
    } finally {
      setBusy(false);
    }
  }

  async function downloadItem(item: MaterialsItem) {
    try {
      await apiClient.downloadItem(item.id, item.name);
      toast.success(`已下载「${item.name}」`);
    } catch (caught) {
      toast.error(errorMessage(caught, "下载失败"));
    }
  }

  async function copyPublicId(publicId: string) {
    try {
      await navigator.clipboard.writeText(publicId);
      toast.success(materialsPublicIdCopySuccessMessage(publicId));
    } catch {
      toast.error("复制失败，请手动选择复制");
    }
  }

  /** PATCH 响应不含 usages 时保留已有引用信息 */
  function mergeItemPreservingUsages(
    next: MaterialsItem,
    previous?: MaterialsItem | null,
  ): MaterialsItem {
    return {
      ...next,
      usages: next.usages ?? previous?.usages,
    };
  }

  function syncPreviewItemInList(next: MaterialsItem) {
    setItems((prev) =>
      prev.map((row) =>
        row.id === next.id ? mergeItemPreservingUsages(next, row) : row,
      ),
    );
    setPreviewItem((prev) =>
      prev?.id === next.id ? mergeItemPreservingUsages(next, prev) : next,
    );
    setEditName(next.name);
    setEditNotes(next.notes ?? "");
  }

  async function savePreviewMeta() {
    if (!previewItem || !canWrite) return;
    const nextName = editName.trim();
    if (!nextName) {
      toast.error("文件名不能为空");
      return;
    }
    const payload: { name?: string; notes?: string | null } = {};
    if (nextName !== previewItem.name) payload.name = nextName;
    const notesVal = editNotes.trim();
    const prevNotes = previewItem.notes ?? "";
    if (notesVal !== prevNotes) payload.notes = notesVal || null;
    if (Object.keys(payload).length === 0) {
      toast.message("没有改动");
      return;
    }

    setBusy(true);
    try {
      const { item } = await apiClient.patchItem(previewItem.id, payload);
      syncPreviewItemInList(item);
      toast.success("已保存素材信息");
    } catch (caught) {
      toast.error(errorMessage(caught, "保存失败"));
    } finally {
      setBusy(false);
    }
  }

  async function confirmDeletePreviewItem() {
    if (!previewItem || !canWrite) return;
    setDeleting(true);
    try {
      await apiClient.deleteItem(previewItem.id);
      setDeleteConfirmOpen(false);
      const deletedName = previewItem.name;
      closePreview();
      toast.success(`已删除「${deletedName}」`);
      await reloadLibrary({ silent: true });
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === "in_use") {
        const usages = extractInUseUsages(caught.payload);
        if (usages && previewItem) {
          syncPreviewItemInList({ ...previewItem, usages });
        }
        toast.error(
          formatMaterialUsagesBlockReason(usages ?? []) ||
            errorMessage(caught, "该素材仍被引用，无法删除"),
        );
        setDeleteConfirmOpen(false);
      } else {
        toast.error(errorMessage(caught, "删除失败"));
      }
    } finally {
      setDeleting(false);
    }
  }

  function revokePreviewObjectUrl() {
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = null;
    }
    setPreviewObjectUrl(null);
  }

  function closePreview() {
    previewRequestIdRef.current += 1;
    revokePreviewObjectUrl();
    setPreviewItem(null);
    setPreviewLoading(false);
    setPreviewMetaLoading(false);
    setPreviewUsagesLoadFailed(false);
    setPreviewError(null);
    setPreviewText(null);
    setEditName("");
    setEditNotes("");
    setDeleteConfirmOpen(false);
    setDeleting(false);
  }

  async function loadPreviewMeta(item: MaterialsItem, requestId: number) {
    setPreviewMetaLoading(true);
    setPreviewUsagesLoadFailed(false);
    try {
      const { item: detail } = await apiClient.getItem(item.id);
      if (requestId !== previewRequestIdRef.current) return;
      syncPreviewItemInList(detail);
    } catch (caught) {
      if (requestId !== previewRequestIdRef.current) return;
      setPreviewUsagesLoadFailed(true);
      toast.error(errorMessage(caught, "无法加载素材详情"));
      setEditName(item.name);
      setEditNotes(item.notes ?? "");
    } finally {
      if (requestId === previewRequestIdRef.current) {
        setPreviewMetaLoading(false);
      }
    }
  }

  async function openPreview(item: MaterialsItem) {
    const requestId = ++previewRequestIdRef.current;
    revokePreviewObjectUrl();
    setPreviewItem(item);
    setPreviewUsagesLoadFailed(false);
    setPreviewError(null);
    setPreviewText(null);
    setPreviewExternalEmbedUrl(null);
    setEditName(item.name);
    setEditNotes(item.notes ?? "");
    void loadPreviewMeta(item, requestId);

    const kind = previewKind(item);
    if (isExternalVideoItem(item)) {
      setPreviewExternalEmbedUrl(externalVideoEmbedUrl(item));
      setPreviewLoading(false);
      return;
    }
    if (kind === "other") {
      setPreviewLoading(false);
      return;
    }

    setPreviewLoading(true);
    try {
      const blob = await apiClient.fetchItemContent(item.id);
      if (requestId !== previewRequestIdRef.current) return;
      if (kind === "image" || kind === "video" || kind === "audio") {
        const url = URL.createObjectURL(blob);
        previewObjectUrlRef.current = url;
        setPreviewObjectUrl(url);
      } else {
        const text = await blob.text();
        if (requestId !== previewRequestIdRef.current) return;
        setPreviewText(text);
      }
    } catch (caught) {
      if (requestId !== previewRequestIdRef.current) return;
      setPreviewError(errorMessage(caught, "无法加载预览"));
    } finally {
      if (requestId === previewRequestIdRef.current) {
        setPreviewLoading(false);
      }
    }
  }

  function markAuditTargetGone(event: MaterialsAuditEvent) {
    const key = auditTargetKey(event);
    setAuditGoneTargets((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }

  /**
   * 操作记录目标是否仍可打开：
   * - Item：已加载 items 命中 → 可点；未命中仍可点（跨 scope），点击时 show 校验
   * - Folder：folders 仍有该目录 → 可点并跳转
   * - 已知 404 / 删除动作 → 不可点
   */
  function auditTargetClickable(event: MaterialsAuditEvent): boolean {
    if (!isOpenableAuditTarget(event)) return false;
    if (auditGoneTargets.has(auditTargetKey(event))) return false;
    if (event.target_type === AUDIT_FOLDER_TARGET) {
      return folders.some((f) => f.id === event.target_id);
    }
    if (event.target_type === AUDIT_ITEM_TARGET) {
      return true;
    }
    return false;
  }

  async function openAuditEventTarget(event: MaterialsAuditEvent) {
    if (!auditTargetClickable(event) || event.target_id == null) return;

    if (event.target_type === AUDIT_FOLDER_TARGET) {
      const folder = folders.find((f) => f.id === event.target_id);
      if (!folder) {
        markAuditTargetGone(event);
        toast.message("内容已不存在");
        return;
      }
      setAuditOpen(false);
      setFolderId(folder.id);
      return;
    }

    if (event.target_type !== AUDIT_ITEM_TARGET) return;

    const local = items.find((i) => i.id === event.target_id);
    if (local) {
      setAuditOpen(false);
      void openPreview(local);
      return;
    }

    try {
      const { item } = await apiClient.getItem(event.target_id);
      setAuditOpen(false);
      void openPreview(item);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 404) {
        markAuditTargetGone(event);
        toast.message("内容已不存在");
        return;
      }
      toast.error(errorMessage(caught, "无法打开该素材"));
    }
  }

  /** 当前预览在可见文件列表中的位置（不含文件夹；不循环） */
  const previewNav = useMemo(() => {
    if (!previewItem) return null;
    const index = filteredVisibleItems.findIndex((i) => i.id === previewItem.id);
    if (index < 0) return null;
    return {
      index,
      total: filteredVisibleItems.length,
      canPrev: index > 0,
      canNext: index < filteredVisibleItems.length - 1,
    };
  }, [previewItem, filteredVisibleItems]);

  function navigatePreview(delta: -1 | 1) {
    if (!previewItem) return;
    const index = filteredVisibleItems.findIndex((i) => i.id === previewItem.id);
    if (index < 0) return;
    const next = index + delta;
    if (next < 0 || next >= filteredVisibleItems.length) return;
    void openPreview(filteredVisibleItems[next]!);
  }

  const navigatePreviewRef = useRef(navigatePreview);
  navigatePreviewRef.current = navigatePreview;

  /** 预览打开时：←/↑ 上一项，→/↓ 下一项（对齐 macOS Quick Look，不循环） */
  useEffect(() => {
    if (previewItem == null) return;

    function onKeyDown(e: KeyboardEvent) {
      const el = e.target as HTMLElement | null;
      if (
        el &&
        (el.tagName === "INPUT" ||
          el.tagName === "TEXTAREA" ||
          el.tagName === "SELECT" ||
          el.isContentEditable)
      ) {
        return;
      }

      let delta: -1 | 1 | 0 = 0;
      if (e.key === "ArrowRight" || e.key === "ArrowDown") delta = 1;
      else if (e.key === "ArrowLeft" || e.key === "ArrowUp") delta = -1;
      else return;

      e.preventDefault();
      navigatePreviewRef.current(delta);
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [previewItem]);

  useEffect(() => {
    return () => {
      if (previewObjectUrlRef.current) {
        URL.revokeObjectURL(previewObjectUrlRef.current);
        previewObjectUrlRef.current = null;
      }
    };
  }, []);

  const filterTags = useMemo((): FilterTag[] => {
    return buildMaterialsFilterTagSpecs(clientFilters, operatorOptions).map((spec) => ({
      key: spec.key,
      facet: spec.facet,
      values: spec.values.map((v) => ({
        key: v.key,
        label: v.label,
        onRemove: () => applyFilterTagClear(v.clear),
      })),
    }));
  }, [clientFilters, operatorOptions]);

  const showFilterTags = hasActiveFilters(clientFilters);
  const activeFilterCount = filterTags.length;

  const previewUsages = previewItem?.usages ?? [];
  const previewUsageRows = useMemo(
    () => buildMaterialUsageListRows(previewUsages, accountId),
    [previewUsages, accountId],
  );
  const previewDeleteBlocked = previewUsages.length > 0;
  const previewMetaDirty = previewItem
    ? editName.trim() !== previewItem.name ||
      editNotes.trim() !== (previewItem.notes ?? "")
    : false;

  return (
    // 抵消 WorkspaceShell 主区 p-4/md:p-6 底边，改为约 5px（不影响其他管理页）
    <div className="-mb-4 flex min-h-0 w-full flex-1 flex-col gap-1.5 overflow-hidden pb-[5px] md:-mb-6">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => onFileChosen(e.target.files)}
      />

      {/* 工具栏：搜索 + 过滤下拉 + 视图 + 操作；筛选条件标签在次行 */}
      <div className="flex shrink-0 flex-col gap-2">
        <div ref={toolbarRowRef} className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[10rem] max-w-xs flex-1 basis-40">
            <Search className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={nameQuery}
              onChange={(e) => setNameQuery(e.target.value)}
              placeholder="搜索当前文件夹"
              className="h-8 rounded-md pl-8"
              aria-label="按名称搜索当前文件夹"
            />
          </div>

          <MaterialsFilterPopover
            open={filterOpen}
            onOpenChange={setFilterOpen}
            accountId={accountId}
            operators={operatorOptions}
            onOperatorsResolved={handleOperatorsResolved}
            clientFilters={clientFilters}
            onClientFiltersChange={setClientFilters}
            activeCount={activeFilterCount}
            toolbarRef={toolbarRowRef}
          />

          <span className="mx-0.5 hidden h-4 w-px bg-border sm:inline-block" />

          <ButtonGroup aria-label="视图切换">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className={cn(
                "h-8 gap-1.5",
                viewMode === "list" &&
                  "border-foreground/25 bg-muted-foreground/15 font-medium text-foreground hover:bg-muted-foreground/20",
              )}
              aria-pressed={viewMode === "list"}
              onClick={() => setViewMode("list")}
            >
              <List className="size-3.5" />
              列表
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className={cn(
                "h-8 gap-1.5",
                viewMode === "grid" &&
                  "border-foreground/25 bg-muted-foreground/15 font-medium text-foreground hover:bg-muted-foreground/20",
              )}
              aria-pressed={viewMode === "grid"}
              onClick={() => setViewMode("grid")}
            >
              <Grid2X2 className="size-3.5" />
              网格
            </Button>
          </ButtonGroup>

          <div className="ml-auto flex flex-wrap items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  className="h-8 gap-1.5"
                  disabled={busy}
                  aria-label="新建"
                >
                  <Plus className="size-3.5" />
                  新建
                  <ChevronDown className="size-3.5 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-44">
                <DropdownMenuItem
                  disabled={busy}
                  onSelect={() => fileInputRef.current?.click()}
                >
                  <Upload className="size-3.5" />
                  上传文件
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={busy}
                  onSelect={() => openComposeDialog()}
                >
                  <FilePenLine className="size-3.5" />
                  新建文案
                </DropdownMenuItem>
                <DropdownMenuItem disabled={busy} onSelect={() => openVideoDialog()}>
                  <Video className="size-3.5" />
                  添加视频
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={busy}
                  onSelect={() => {
                    setNewFolderName("");
                    setFolderDialogOpen(true);
                  }}
                >
                  <FolderPlus className="size-3.5" />
                  新建文件夹
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button type="button" variant="outline" size="sm" onClick={() => void openAudit()}>
              <ScrollText className="size-3.5" />
              操作记录
            </Button>
          </div>
        </div>

        {showFilterTags ? (
          <div className="flex flex-wrap items-center gap-1.5">
            {filterTags.map((tag) => (
              <span
                key={tag.key}
                className="inline-flex h-7 max-w-full items-center gap-1 rounded-md border border-border bg-muted/40 px-2 text-xs text-foreground"
              >
                <span className="shrink-0 text-muted-foreground">{tag.facet}:</span>
                <span className="flex min-w-0 flex-wrap items-center gap-1">
                  {tag.values.map((v) => (
                    <span
                      key={v.key}
                      className="inline-flex max-w-full items-center gap-0.5 rounded bg-background/80 px-1 py-0.5"
                    >
                      <span className="truncate">{v.label}</span>
                      <button
                        type="button"
                        className="inline-flex size-3.5 shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground"
                        aria-label={`移除筛选 ${tag.facet}: ${v.label}`}
                        onClick={v.onRemove}
                      >
                        <X className="size-2.5" />
                      </button>
                    </span>
                  ))}
                </span>
              </span>
            ))}
          </div>
        ) : null}
      </div>

      <ResizablePanelGroup
        orientation="horizontal"
        className="min-h-0 flex-1 overflow-hidden rounded-lg border border-border bg-card"
      >
        <ResizablePanel
          defaultSize={200}
          minSize={140}
          maxSize={420}
          groupResizeBehavior="preserve-pixel-size"
          className="min-h-0"
        >
          <aside className="flex h-full min-h-0 flex-col bg-muted/20">
            <div className="flex shrink-0 items-center justify-between gap-2 px-3 pt-3 pb-2">
              <p className="text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
                {contextCopy.title}
              </p>
              <button
                type="button"
                role="switch"
                aria-checked={includeDescendants}
                aria-label="显示当前目录下的所有子孙文件"
                title="开启后列出当前目录及子文件夹中的全部文件"
                className={cn(
                  "rounded-md px-1.5 py-0.5 text-[11px] font-medium transition-colors",
                  includeDescendants
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
                onClick={() => setIncludeDescendants((v) => !v)}
              >
                含子目录
              </button>
            </div>
            <MaterialsFolderTree
              folders={folders}
              selectedFolderId={folderId}
              workRootId={workRootId}
              rootLabel={contextCopy.rootLabel}
              loading={loading}
              onNavigate={navigateFolder}
              canWrite={canWrite}
              folderIsEmpty={folderEmptyCheck}
              onRenameFolder={beginRenameFolder}
              onDeleteFolder={beginDeleteFolder}
              onMoveFolder={beginMoveFolder}
            />
          </aside>
        </ResizablePanel>

        <ResizableHandle withHandle />

        <ResizablePanel minSize={280} className="min-h-0">
          <section className="flex h-full min-h-0 min-w-0 flex-col">
            <div className="flex h-9 shrink-0 flex-nowrap items-center gap-2 overflow-hidden border-b border-border px-3 text-xs text-muted-foreground">
              {selected.size > 0 ? (
                <>
                  <span className="min-w-0 truncate leading-none">
                    已选 <strong className="text-foreground">{selected.size}</strong> 项
                    {selectedFolderCount > 0 || selectedItemIds.length > 0 ? (
                      <span className="text-muted-foreground">
                        （{selectedItemIds.length} 个文件
                        {selectedFolderCount > 0 ? `、${selectedFolderCount} 个文件夹` : ""}）
                      </span>
                    ) : null}
                  </span>
                  <div className="ml-auto flex shrink-0 items-center gap-1.5">
                    {canWrite &&
                    (selectedItemIds.length > 0 || selectedFolderIds.length > 0) ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-6 gap-1 px-2 text-[11px]"
                        onClick={openMove}
                      >
                        <FolderInput className="size-3" />
                        移动
                      </Button>
                    ) : null}
                    {canWrite &&
                    (selectedItemIds.length > 0 || selectedFolderIds.length > 0) ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-6 gap-1 px-2 text-[11px] text-destructive hover:text-destructive"
                        onClick={openBulkDelete}
                      >
                        <Trash2 className="size-3" />
                        删除
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      size="sm"
                      className="h-6 gap-1 px-2 text-[11px]"
                      onClick={openExtract}
                    >
                      <Sparkles className="size-3" />
                      提取到知识库
                    </Button>
                  </div>
                </>
              ) : (
                breadcrumb.map((b, i) => (
                  <span key={b.id ?? ROOT_KEY} className="flex items-center gap-1 leading-none">
                    {i > 0 ? <span>/</span> : null}
                    <button
                      type="button"
                      className="hover:text-foreground hover:underline"
                      onClick={() => navigateFolder(b.id)}
                    >
                      {b.name}
                    </button>
                  </span>
                ))
              )}
            </div>

            <div className="min-h-0 flex-1 overflow-auto">
              {loading ? (
                <div className="px-3 py-10 text-center text-sm text-muted-foreground">
                  加载中…
                </div>
              ) : viewMode === "list" ? (
                <table className="w-full text-sm">
                  <thead className="sticky top-0 z-10 bg-card">
                    <tr className="border-b border-border text-left text-xs text-muted-foreground">
                      <th className="w-10 px-3 py-2">
                        <input
                          ref={selectAllRef}
                          type="checkbox"
                          className="size-3.5 accent-primary"
                          checked={allVisibleSelected}
                          disabled={visibleSelectKeys.length === 0}
                          onChange={toggleSelectAllVisible}
                          aria-label="全选当前列表"
                        />
                      </th>
                      <th className="px-2 py-2 font-medium">名称</th>
                      <th className="px-2 py-2 font-medium">大小</th>
                      <th className="px-2 py-2 font-medium">上传者</th>
                      <th className="px-2 py-2 font-medium">时间</th>
                      <th className="w-20 px-2 py-2 font-medium" />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredChildFolders.map((f) => (
                      <tr
                        key={`dir-${f.id}`}
                        className="group border-b border-border/60 hover:bg-muted/30"
                      >
                        <td className="px-3 py-2">
                          {canManageFolder(f) ? (
                            <input
                              type="checkbox"
                              className="size-3.5 accent-primary"
                              checked={selected.has(`folder:${f.id}`)}
                              onChange={() => toggleSelect(`folder:${f.id}`)}
                              aria-label={`选择文件夹 ${f.name}`}
                            />
                          ) : null}
                        </td>
                        <td className="px-2 py-2">
                          <button
                            type="button"
                            className="inline-flex items-start gap-1.5 font-medium hover:underline"
                            onClick={() => navigateFolder(f.id)}
                          >
                            <FolderGlyph folder={f} className="size-4" />
                            {f.name}
                          </button>
                        </td>
                        <td className="px-2 py-2 text-muted-foreground">—</td>
                        <td className="px-2 py-2 text-muted-foreground">—</td>
                        <td className="px-2 py-2 text-muted-foreground">—</td>
                        <td className="px-2 py-2 text-right">
                          {canManageFolder(f) ? (
                            <MaterialsFolderActionsMenu
                              canWrite={canWrite}
                              isEmpty={folderEmptyCheck(f.id)}
                              onRename={() => beginRenameFolder(f)}
                              onMove={() => beginMoveFolder(f)}
                              onDelete={() => beginDeleteFolder(f)}
                            />
                          ) : null}
                        </td>
                      </tr>
                    ))}
                    {filteredVisibleItems.map((item) => {
                      const nestedFolderLabel = folderLabelForItem(item);
                      return (
                      <tr
                        key={item.id}
                        className={cn(
                          "cursor-pointer border-b border-border/60 hover:bg-muted/30",
                          selected.has(String(item.id)) && "bg-primary/5",
                        )}
                        onClick={() => toggleSelect(String(item.id))}
                      >
                        <td
                          className="px-3 py-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            className="size-3.5 accent-primary"
                            checked={selected.has(String(item.id))}
                            onChange={() => toggleSelect(String(item.id))}
                            aria-label={`选择 ${item.name}`}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <span className="inline-flex items-start gap-1.5">
                            <FileText className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                            <span className="min-w-0">
                              <button
                                type="button"
                                className="text-left font-medium hover:underline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void openPreview(item);
                                }}
                              >
                                {item.name}
                              </button>
                              {nestedFolderLabel ? (
                                <span className="mt-0.5 block text-[11px] text-muted-foreground">
                                  {nestedFolderLabel}
                                </span>
                              ) : null}
                              <button
                                type="button"
                                className="mt-0.5 inline-flex max-w-full items-center gap-1 font-mono text-[11px] text-muted-foreground hover:text-foreground"
                                title="复制素材编号"
                                aria-label={`复制素材编号 ${item.slug}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void copyPublicId(item.slug);
                                }}
                              >
                                <span className="truncate">{item.slug}</span>
                                <Copy className="size-3 shrink-0 opacity-60" />
                              </button>
                            </span>
                            {isMarkdown(item) ? (
                              <Badge variant="secondary" className="text-[10px]">
                                文案·md
                              </Badge>
                            ) : null}
                          </span>
                        </td>
                        <td className="px-2 py-2 text-muted-foreground">
                          {formatBytes(item.byte_size)}
                        </td>
                        <td className="px-2 py-2 text-muted-foreground">
                          {item.uploader_name || "—"}
                        </td>
                        <td className="px-2 py-2 text-muted-foreground">
                          {formatTime(item.updated_at)}
                        </td>
                        <td
                          className="px-2 py-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2"
                            onClick={() => void downloadItem(item)}
                            aria-label={`下载 ${item.name}`}
                          >
                            <Download className="size-3.5" />
                          </Button>
                        </td>
                      </tr>
                      );
                    })}
                    {folderEmpty ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-3 py-10 text-center text-sm text-muted-foreground"
                        >
                          {libraryEmpty ? (
                            <div className="space-y-3">
                              <p>暂无素材</p>
                              <div className="flex flex-wrap justify-center gap-2">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => fileInputRef.current?.click()}
                                >
                                  上传文件
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  onClick={() => openComposeDialog()}
                                >
                                  新建文案
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setFolderDialogOpen(true)}
                                >
                                  新建文件夹
                                </Button>
                              </div>
                            </div>
                          ) : (
                            "此文件夹暂无内容（可「新建文案」或上传）"
                          )}
                        </td>
                      </tr>
                    ) : null}
                    {searchEmpty ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-3 py-10 text-center text-sm text-muted-foreground"
                        >
                          无匹配「{nameQuery.trim()}」的文件或文件夹
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              ) : (
                <div className="grid grid-cols-2 gap-3 p-3 sm:grid-cols-3 lg:grid-cols-4">
                  {filteredChildFolders.map((f) => (
                    <div
                      key={f.id}
                      className="group relative flex flex-col items-start gap-2 rounded-lg border border-border p-3 text-left hover:bg-muted/40"
                    >
                      <div className="absolute top-2 right-2">
                        {canManageFolder(f) ? (
                          <MaterialsFolderActionsMenu
                            canWrite={canWrite}
                            isEmpty={folderEmptyCheck(f.id)}
                            compact
                            onRename={() => beginRenameFolder(f)}
                            onMove={() => beginMoveFolder(f)}
                            onDelete={() => beginDeleteFolder(f)}
                          />
                        ) : null}
                      </div>
                      <button
                        type="button"
                        className="flex w-full flex-col items-start gap-2 text-left"
                        onClick={() => navigateFolder(f.id)}
                      >
                        <FolderGlyph folder={f} className="size-8" />
                        <span className="line-clamp-2 pr-7 text-sm font-medium">{f.name}</span>
                      </button>
                    </div>
                  ))}
                  {filteredVisibleItems.map((item) => (
                    <div
                      key={item.id}
                      role="button"
                      tabIndex={0}
                      className={cn(
                        "relative flex cursor-pointer flex-col gap-2 rounded-lg border border-border p-3 text-left",
                        selected.has(String(item.id)) && "border-primary bg-primary/5",
                      )}
                      onClick={() => toggleSelect(String(item.id))}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggleSelect(String(item.id));
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        className="absolute top-2 right-2 size-3.5 accent-primary"
                        checked={selected.has(String(item.id))}
                        onChange={() => toggleSelect(String(item.id))}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`选择 ${item.name}`}
                      />
                      <FileText className="size-8 shrink-0 text-muted-foreground" />
                      <button
                        type="button"
                        className="line-clamp-2 pr-5 text-left text-sm font-medium hover:underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          void openPreview(item);
                        }}
                      >
                        {item.name}
                      </button>
                      {folderLabelForItem(item) ? (
                        <span className="text-[11px] text-muted-foreground">
                          {folderLabelForItem(item)}
                        </span>
                      ) : null}
                      <button
                        type="button"
                        className="inline-flex max-w-full items-center gap-1 font-mono text-[11px] text-muted-foreground hover:text-foreground"
                        title="复制素材编号"
                        aria-label={`复制素材编号 ${item.slug}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          void copyPublicId(item.slug);
                        }}
                      >
                        <span className="truncate">{item.slug}</span>
                        <Copy className="size-3 shrink-0 opacity-60" />
                      </button>
                      <span className="text-xs text-muted-foreground">
                        {item.uploader_name || "—"} · {formatTime(item.updated_at)}
                      </span>
                    </div>
                  ))}
                  {folderEmpty ? (
                    <div className="col-span-full py-10 text-center text-sm text-muted-foreground">
                      {libraryEmpty ? "暂无素材" : "此文件夹暂无内容"}
                    </div>
                  ) : null}
                  {searchEmpty ? (
                    <div className="col-span-full py-10 text-center text-sm text-muted-foreground">
                      无匹配「{nameQuery.trim()}」的文件或文件夹
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </section>
        </ResizablePanel>
      </ResizablePanelGroup>

      {/* 右侧留白避开 Hermes FAB（fixed bottom-6 right-6 size-14） */}
      <MaterialsStorageBar
        items={items}
        className="mr-20 max-w-[calc(100%-5rem)]"
      />


      <Sheet open={auditOpen} onOpenChange={setAuditOpen}>
        <SheetContent
          side="right"
          className="flex h-full w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-md"
        >
          <SheetHeader className="shrink-0 border-b border-border/60 px-6 py-4 pr-14 text-left">
            <SheetTitle>操作记录</SheetTitle>
            <SheetDescription>
              含上传、新建文案与下载。与账户审计日志分开。
            </SheetDescription>
          </SheetHeader>
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-6 py-4">
            {auditLoading ? (
              <div className="flex flex-1 items-center justify-center py-8">
                <p className="text-sm text-muted-foreground">加载中…</p>
              </div>
            ) : auditEvents.length === 0 ? (
              <div className="flex flex-1 items-center justify-center py-8">
                <p className="text-sm text-muted-foreground">暂无操作记录</p>
              </div>
            ) : (
              <ul className="space-y-2">
                {auditEvents.map((e) => {
                  const fileName =
                    typeof e.payload?.name === "string"
                      ? e.payload.name
                      : `#${e.target_id ?? "—"}`;
                  const clickable = auditTargetClickable(e);
                  return (
                    <li
                      key={e.id}
                      className={cn(
                        "rounded-lg border border-border/60 bg-muted/30 px-3 py-2.5 dark:bg-muted/20",
                        clickable &&
                          "cursor-pointer transition-colors hover:border-border hover:bg-muted/50 dark:hover:bg-muted/35",
                      )}
                      role={clickable ? "button" : undefined}
                      tabIndex={clickable ? 0 : undefined}
                      onClick={
                        clickable
                          ? () => {
                              void openAuditEventTarget(e);
                            }
                          : undefined
                      }
                      onKeyDown={
                        clickable
                          ? (ev) => {
                              if (ev.key === "Enter" || ev.key === " ") {
                                ev.preventDefault();
                                void openAuditEventTarget(e);
                              }
                            }
                          : undefined
                      }
                    >
                      <div className="truncate text-xs tabular-nums text-muted-foreground">
                        {formatTime(e.created_at)} · {e.actor_name || "系统"}
                      </div>
                      <div className="mt-1 flex min-w-0 items-baseline gap-1.5 text-sm leading-snug">
                        <span className="shrink-0 font-medium text-foreground">
                          {auditActionLabel(e.action)}
                        </span>
                        <span className="shrink-0 text-muted-foreground">·</span>
                        <span
                          className="min-w-0 truncate text-muted-foreground"
                          title={fileName}
                        >
                          {fileName}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <Dialog
        open={composeOpen}
        onOpenChange={(open) => {
          setComposeOpen(open);
          if (open) {
            setComposeTitle("");
            setComposeBody("");
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>新建文案</DialogTitle>
            <DialogDescription>
              保存为当前文件夹里的 <strong>Markdown 文件（.md）</strong>
              ，与上传的 PDF/图片同一素材模型——不是独立「文案块」类型。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <label className="block space-y-1.5 text-sm">
              <span className="text-muted-foreground">文件名</span>
              <input
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                value={composeTitle}
                onChange={(e) => setComposeTitle(e.target.value)}
                placeholder="企业简介-草稿"
              />
            </label>
            <label className="block space-y-1.5 text-sm">
              <span className="text-muted-foreground">正文（Markdown）</span>
              <textarea
                className="min-h-36 w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-sm"
                value={composeBody}
                onChange={(e) => setComposeBody(e.target.value)}
                placeholder="在此粘贴或撰写可复用文案。保存后将作为 Markdown 文件出现在当前文件夹。"
              />
            </label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setComposeOpen(false)}>
              取消
            </Button>
            <Button type="button" disabled={busy} onClick={() => void saveMarkdownCompose()}>
              保存为 .md
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={videoDialogOpen}
        onOpenChange={(open) => {
          setVideoDialogOpen(open);
          if (open) setVideoInput("");
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>添加视频</DialogTitle>
            <DialogDescription>
              粘贴腾讯视频或 B 站的<strong>插入代码</strong>，或<strong>视频链接</strong>。其它地址无法添加。
            </DialogDescription>
          </DialogHeader>
          <label className="block space-y-1.5 text-sm">
            <span className="text-muted-foreground">插入代码或链接</span>
            <Textarea
              className="min-h-28 font-mono text-sm"
              value={videoInput}
              onChange={(e) => setVideoInput(e.target.value)}
              placeholder="粘贴 iframe 插入代码，或 https://v.qq.com/… / bilibili.com/video/…"
            />
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setVideoDialogOpen(false)}>
              取消
            </Button>
            <Button type="button" disabled={busy} onClick={() => void saveExternalVideo()}>
              添加
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={folderDialogOpen} onOpenChange={setFolderDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>新建文件夹</DialogTitle>
            <DialogDescription>
              {folderId == null
                ? "将创建在「全部素材」根目录下。"
                : `将创建在当前文件夹下。`}
            </DialogDescription>
          </DialogHeader>
          <label className="block space-y-1.5 text-sm">
            <span className="text-muted-foreground">名称</span>
            <input
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="企业简介"
              onKeyDown={(e) => {
                if (e.key === "Enter") void createFolder();
              }}
            />
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setFolderDialogOpen(false)}>
              取消
            </Button>
            <Button type="button" disabled={busy} onClick={() => void createFolder()}>
              创建
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={previewItem != null}
        onOpenChange={(open) => {
          if (!open) closePreview();
        }}
      >
        <DialogContent className={MATERIALS_PREVIEW_DIALOG_CLASS}>
          {previewItem ? (
            <>
              <DialogHeader className="shrink-0">
                <div className="flex items-start gap-2 pr-8">
                  <DialogTitle className="min-w-0 flex-1 break-all">
                    {previewItem.name}
                  </DialogTitle>
                  {previewNav && previewNav.total > 1 ? (
                    <div className="flex shrink-0 items-center gap-0.5 pt-0.5">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        disabled={!previewNav.canPrev}
                        aria-label="上一个文件"
                        onClick={() => navigatePreview(-1)}
                      >
                        <ChevronLeft className="size-4" />
                      </Button>
                      <span className="min-w-[3.5rem] text-center text-xs tabular-nums text-muted-foreground">
                        {previewNav.index + 1} / {previewNav.total}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        disabled={!previewNav.canNext}
                        aria-label="下一个文件"
                        onClick={() => navigatePreview(1)}
                      >
                        <ChevronRight className="size-4" />
                      </Button>
                    </div>
                  ) : null}
                </div>
                <DialogDescription>
                  {previewKindLabel(previewKind(previewItem))} ·{" "}
                  {formatBytes(previewItem.byte_size)}
                  {previewItem.uploader_name
                    ? ` · ${previewItem.uploader_name}`
                    : ""}
                  {" · "}
                  {formatTime(previewItem.updated_at)}
                </DialogDescription>
              </DialogHeader>

              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                {previewLoading ? (
                  <p className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
                    加载预览…
                  </p>
                ) : previewError ? (
                  <p className="flex flex-1 items-center justify-center text-sm text-destructive">
                    {previewError}
                  </p>
                ) : previewKind(previewItem) === "image" && previewObjectUrl ? (
                  <div className="-mx-1 flex min-h-0 flex-1 items-center justify-center overflow-auto rounded-lg bg-muted/40 p-2">
                    <img
                      src={previewObjectUrl}
                      alt={previewItem.name}
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                ) : previewKind(previewItem) === "markdown" ? (
                  <ScrollArea className="min-h-0 flex-1 rounded-lg border border-border px-4 py-3">
                    {previewText != null && previewText.trim() ? (
                      <MessageResponse
                        className={cn(
                          "max-w-none text-sm leading-relaxed text-foreground",
                          "[&>*:first-child]:mt-0 [&>*:last-child]:mb-0",
                          "[&_h1]:mb-3 [&_h1]:mt-0 [&_h1]:text-xl [&_h1]:font-semibold",
                          "[&_h2]:mb-2 [&_h2]:mt-5 [&_h2]:text-lg [&_h2]:font-semibold",
                          "[&_p]:my-2 [&_ul]:my-2 [&_ol]:my-2 [&_li]:my-0.5",
                          "[&_pre]:my-3 [&_code]:text-[0.9em]",
                        )}
                        mode="static"
                      >
                        {previewText}
                      </MessageResponse>
                    ) : (
                      <pre className="whitespace-pre-wrap font-mono text-sm text-muted-foreground">
                        {previewText ?? "（空文件）"}
                      </pre>
                    )}
                  </ScrollArea>
                ) : previewKind(previewItem) === "video" && previewExternalEmbedUrl ? (
                  <div className="-mx-1 flex min-h-0 flex-1 items-center justify-center rounded-lg bg-muted/40 p-2">
                    <div className="aspect-video w-full max-w-3xl overflow-hidden rounded-md border border-border bg-black">
                      <iframe
                        title={previewItem.name}
                        src={previewExternalEmbedUrl}
                        className="h-full w-full border-0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        loading="lazy"
                        allowFullScreen
                      />
                    </div>
                  </div>
                ) : previewKind(previewItem) === "video" && previewObjectUrl ? (
                  <div className="-mx-1 flex min-h-0 flex-1 items-center justify-center rounded-lg bg-muted/40 p-2">
                    <video
                      controls
                      src={previewObjectUrl}
                      className="max-h-full w-full rounded-md bg-black object-contain"
                    >
                      您的浏览器不支持视频播放。
                    </video>
                  </div>
                ) : previewKind(previewItem) === "audio" && previewObjectUrl ? (
                  <div className="flex min-h-0 flex-1 items-center rounded-lg border border-border bg-muted/30 px-4 py-5">
                    <audio controls src={previewObjectUrl} className="w-full">
                      您的浏览器不支持音频播放。
                    </audio>
                  </div>
                ) : (
                  <div className="flex min-h-0 flex-1 flex-col justify-center rounded-lg border border-border bg-muted/30 px-4 py-5 text-sm">
                    <p className="font-medium text-foreground">暂不支持应用内预览</p>
                    <p className="mt-1 text-muted-foreground">
                      可下载后用本地应用打开（文档 / 压缩包等）。
                    </p>
                    <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-xs">
                      <dt className="text-muted-foreground">类型</dt>
                      <dd>{previewItem.content_type || "未知"}</dd>
                      <dt className="text-muted-foreground">大小</dt>
                      <dd>{formatBytes(previewItem.byte_size)}</dd>
                      <dt className="text-muted-foreground">上传者</dt>
                      <dd>{previewItem.uploader_name || "—"}</dd>
                      <dt className="text-muted-foreground">更新</dt>
                      <dd>{formatTime(previewItem.updated_at)}</dd>
                    </dl>
                  </div>
                )}
              </div>

              <div className="max-h-64 shrink-0 space-y-3 overflow-y-auto rounded-lg border border-border bg-muted/20 px-4 py-3 text-sm">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-muted-foreground">素材编号</p>
                    <p className="mt-0.5 font-mono text-sm break-all">{previewItem.slug}</p>
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      供 Hermes / 品牌站引用；成员可复制，经理可改备注
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 shrink-0 gap-1.5"
                    disabled={previewMetaLoading}
                    onClick={() => void copyPublicId(previewItem.slug)}
                  >
                    <Copy className="size-3.5" />
                    复制编号
                  </Button>
                </div>

                {previewMetaLoading ? (
                  <p className="text-xs text-muted-foreground">正在加载引用信息…</p>
                ) : previewUsagesLoadFailed ? (
                  <p className="text-xs text-muted-foreground">
                    引用信息加载失败，暂不可删除
                  </p>
                ) : null}

                {canWrite ? (
                  <div className="space-y-3 border-t border-border/60 pt-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="material-edit-name" className="text-xs text-muted-foreground">
                        文件名
                      </Label>
                      <Input
                        id="material-edit-name"
                        value={editName}
                        disabled={busy || previewMetaLoading}
                        onChange={(e) => setEditName(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="material-edit-notes" className="text-xs text-muted-foreground">
                        备注（选填）
                      </Label>
                      <Textarea
                        id="material-edit-notes"
                        value={editNotes}
                        disabled={busy || previewMetaLoading}
                        placeholder="例如：主 Logo、官网首屏配图"
                        className="min-h-16 resize-y text-sm"
                        onChange={(e) => setEditNotes(e.target.value)}
                      />
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={busy || previewMetaLoading || !previewMetaDirty}
                      onClick={() => void savePreviewMeta()}
                    >
                      保存名称与备注
                    </Button>
                  </div>
                ) : previewItem.notes?.trim() ? (
                  <div className="border-t border-border/60 pt-3">
                    <p className="text-xs font-medium text-muted-foreground">备注</p>
                    <p className="mt-1 whitespace-pre-wrap text-sm">{previewItem.notes}</p>
                  </div>
                ) : null}

                {previewDeleteBlocked ? (
                  <div className="space-y-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2">
                    <p className="text-xs font-medium text-destructive">
                      {formatMaterialUsagesBlockReason(previewUsages)}
                    </p>
                    <ul className="space-y-2">
                      {previewUsageRows.map((row) => (
                        <li
                          key={row.key}
                          className="flex flex-wrap items-start justify-between gap-2 rounded-md border border-border/60 bg-background/80 px-2.5 py-2"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-xs font-medium text-foreground">
                              {row.siteTitle}
                              <span className="text-muted-foreground"> · </span>
                              {row.pageLabel}
                            </p>
                            {row.frontendUrl ? (
                              <p className="mt-0.5 truncate font-mono text-[11px] text-muted-foreground">
                                {row.frontendUrl}
                              </p>
                            ) : null}
                          </div>
                          <div className="flex shrink-0 items-center gap-1">
                            {row.frontendUrl ? (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="h-7 gap-1 px-2 text-[11px]"
                                asChild
                              >
                                <a
                                  href={row.frontendUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <ExternalLink className="size-3" />
                                  前台
                                </a>
                              </Button>
                            ) : null}
                            {row.backendPath ? (
                              <Button
                                type="button"
                                size="sm"
                                variant="secondary"
                                className="h-7 px-2 text-[11px]"
                                asChild
                              >
                                <Link to={row.backendPath} onClick={closePreview}>
                                  后台
                                </Link>
                              </Button>
                            ) : null}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>

              <DialogFooter className="shrink-0 flex-col gap-2 sm:flex-row sm:justify-between">
                {canWrite ? (
                  <Button
                    type="button"
                    variant="destructive"
                    className="gap-1.5 sm:mr-auto"
                    disabled={
                      previewDeleteBlocked ||
                      deleting ||
                      previewMetaLoading ||
                      previewUsagesLoadFailed
                    }
                    title={
                      previewDeleteBlocked
                        ? formatMaterialUsagesBlockReason(previewUsages)
                        : previewUsagesLoadFailed
                          ? "引用信息加载失败，暂不可删除"
                          : undefined
                    }
                    onClick={() => setDeleteConfirmOpen(true)}
                  >
                    <Trash2 className="size-3.5" />
                    删除素材
                  </Button>
                ) : (
                  <span className="text-xs text-muted-foreground sm:mr-auto">
                    仅经理可改名、改备注或删除
                  </span>
                )}
                <div className="flex flex-wrap gap-2 sm:justify-end">
                  <Button type="button" variant="outline" onClick={closePreview}>
                    关闭
                  </Button>
                  <Button
                    type="button"
                    onClick={() => void downloadItem(previewItem)}
                  >
                    <Download className="size-3.5" />
                    下载
                  </Button>
                </div>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title="删除素材？"
        description={
          previewItem ? (
            <span>
              将永久删除「<strong>{previewItem.name}</strong>」，此操作不可撤销。
            </span>
          ) : (
            "将永久删除该素材，此操作不可撤销。"
          )
        }
        confirmLabel="删除"
        tone="destructive"
        confirming={deleting}
        onConfirm={() => void confirmDeletePreviewItem()}
      />

      <ConfirmDialog
        open={bulkDeleteConfirmOpen}
        onOpenChange={setBulkDeleteConfirmOpen}
        title={`删除 ${selected.size} 项？`}
        description={
          <span>
            将永久删除已选内容
            {selectedItemIds.length > 0 || selectedFolderCount > 0 ? (
              <>
                （{selectedItemIds.length} 个文件
                {selectedFolderCount > 0 ? `、${selectedFolderCount} 个文件夹` : ""}）
              </>
            ) : null}
            。非空文件夹与仍被品牌站引用的素材会跳过。此操作不可撤销。
          </span>
        }
        confirmLabel="删除"
        tone="destructive"
        confirming={bulkDeleting}
        onConfirm={() => void confirmBulkDelete()}
      />

      <ConfirmDialog
        open={folderDeleteConfirmOpen}
        onOpenChange={setFolderDeleteConfirmOpen}
        title="删除空文件夹？"
        description={
          <span>
            将永久删除空文件夹「
            <strong>
              {folders.find((f) => f.id === folderDeleteIds[0])?.name ?? "文件夹"}
            </strong>
            」，此操作不可撤销。
          </span>
        }
        confirmLabel="删除"
        tone="destructive"
        confirming={folderDeleting}
        onConfirm={() => void confirmDeleteFolders()}
      />

      <Dialog
        open={renameFolderOpen}
        onOpenChange={(open) => {
          setRenameFolderOpen(open);
          if (!open) setRenameFolderTarget(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>重命名文件夹</DialogTitle>
            <DialogDescription>输入新的文件夹名称。</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="rename-folder-name">名称</Label>
            <Input
              id="rename-folder-name"
              value={renameFolderName}
              onChange={(e) => setRenameFolderName(e.target.value)}
              disabled={renamingFolder}
              onKeyDown={(e) => {
                if (e.key === "Enter") void confirmRenameFolder();
              }}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={renamingFolder}
              onClick={() => setRenameFolderOpen(false)}
            >
              取消
            </Button>
            <Button
              type="button"
              disabled={renamingFolder}
              onClick={() => void confirmRenameFolder()}
            >
              {renamingFolder ? "保存中…" : "保存"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>移动到哪个文件夹</DialogTitle>
            <DialogDescription>
              已选 {selectedItemIds.length} 个文件
              {selectedFolderCount > 0 ? `、${selectedFolderCount} 个文件夹` : ""}
              。文件夹会连同子目录一起移动。
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-64 rounded-md border border-border">
            <ul className="p-1 text-sm">
              {movePickerRows.map((row) => {
                const selectedTarget = moveTargetId === row.id;
                return (
                  <li key={row.id ?? ROOT_KEY}>
                    <button
                      type="button"
                      disabled={row.disabled || moving}
                      className={cn(
                        "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left",
                        row.disabled
                          ? "cursor-not-allowed text-muted-foreground/50"
                          : "hover:bg-muted",
                        selectedTarget && !row.disabled && "bg-muted font-medium",
                      )}
                      style={{ paddingLeft: `${8 + row.depth * 14}px` }}
                      onClick={() => {
                        if (!row.disabled) setMoveTargetId(row.id);
                      }}
                    >
                      {row.folder ? (
                        <FolderGlyph folder={row.folder} className="size-3.5" />
                      ) : (
                        <Folder className="size-3.5 shrink-0 text-muted-foreground" />
                      )}
                      <span className="truncate">{row.name}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </ScrollArea>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={moving}
              onClick={() => setMoveOpen(false)}
            >
              取消
            </Button>
            <Button
              type="button"
              disabled={moving}
              onClick={() => void confirmMove()}
            >
              {moving ? "移动中…" : "移动到此处"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={extractOpen} onOpenChange={setExtractOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>提取到知识库</DialogTitle>
            <DialogDescription>
              已选 {selected.size} 项。
              <Badge variant="outline" className="ml-2 align-middle">
                即将支持
              </Badge>
              <span className="mt-1 block text-xs">
                预览示意如下；确认不会写入知识库（Wave B）。
              </span>
            </DialogDescription>
          </DialogHeader>
          <ul className="space-y-2 py-2">
            {EXTRACT_COMING_SOON_FACTS.map((f) => (
              <li key={f.id}>
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5 size-3.5 accent-primary"
                    checked={Boolean(factChecks[f.id])}
                    onChange={(e) =>
                      setFactChecks((prev) => ({
                        ...prev,
                        [f.id]: e.target.checked,
                      }))
                    }
                  />
                  <span className="text-muted-foreground">{f.text}</span>
                </label>
              </li>
            ))}
          </ul>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setExtractOpen(false)}>
              取消
            </Button>
            <Button type="button" onClick={confirmExtractComingSoon}>
              知道了
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
